import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, CheckCircle, ExternalLink, Building2, CreditCard } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ROUTE_PATHS } from '@/lib/index';
import { fundingMatches } from '@/lib/dashboard-data';
import { springPresets } from '@/lib/motion';

export default function BusinessFunding() {
  const [selectedMatch, setSelectedMatch] = useState<string | null>(null);
  const fundingReadiness = 85;

  const readinessSteps = [
    { id: '1', text: 'Business registered (EIN obtained)', completed: true },
    { id: '2', text: 'Credit score above 680', completed: true },
    { id: '3', text: 'Business bank account opened', completed: true },
    { id: '4', text: '6+ months in business', completed: true },
    { id: '5', text: 'Business address verified', completed: false },
    { id: '6', text: 'Financial statements prepared', completed: false },
  ];

  const naicsCodes = [
    { code: '541611', title: 'Administrative Management Consulting', risk: 'Low', recommended: true },
    { code: '541618', title: 'Other Management Consulting', risk: 'Low', recommended: false },
    { code: '541990', title: 'All Other Professional Services', risk: 'Medium', recommended: false },
    { code: '812990', title: 'All Other Personal Services', risk: 'High', recommended: false },
  ];

  const tradeLines = [
    { name: 'Uline', limit: '$5,000', terms: 'Net-30', reports: 'Yes', difficulty: 'Easy' },
    { name: 'Quill', limit: '$2,500', terms: 'Net-30', reports: 'Yes', difficulty: 'Easy' },
    { name: 'Grainger', limit: '$10,000', terms: 'Net-60', reports: 'Yes', difficulty: 'Medium' },
    { name: 'Crown Office', limit: '$3,000', terms: 'Net-30', reports: 'Yes', difficulty: 'Easy' },
  ];

  const zeroPercentSteps = [
    { phase: 1, title: 'Foundation', description: 'Get 3 Tier-1 trade lines reporting', amount: '$10k-$15k' },
    { phase: 2, title: 'Business Cards', description: 'Apply for Chase Ink, AMEX Blue Business', amount: '$25k-$50k' },
    { phase: 3, title: 'Store Cards', description: 'Home Depot, Lowe\'s, Office Depot business cards', amount: '$15k-$30k' },
    { phase: 4, title: 'Balance Transfers', description: 'Transfer to 0% APR cards, stack limits', amount: '$50k-$100k' },
    { phase: 5, title: 'Advanced Stacking', description: 'Multiple 0% cards, business lines of credit', amount: '$100k-$150k' },
  ];

  const getMatchColor = (score: number) => {
    if (score >= 80) return 'text-primary border-primary bg-primary/10';
    if (score >= 60) return 'text-accent border-accent bg-accent/10';
    return 'text-muted-foreground border-border bg-muted/10';
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-4">
            <Link to={ROUTE_PATHS.DASHBOARD}>
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold">Business Funding Module</h1>
              <p className="text-muted-foreground mt-1">Get matched with funding, build business credit, access capital</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* Funding Readiness Score */}
        <Card className="p-6 mb-8 bg-primary/5 border-primary/20">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-2xl font-bold mb-1">Funding Readiness Score</h3>
              <p className="text-sm text-muted-foreground">Complete all steps to maximize approval odds</p>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-primary font-mono">{fundingReadiness}%</div>
              <div className="text-xs text-muted-foreground mt-1">Ready to Apply</div>
            </div>
          </div>
          <Progress value={fundingReadiness} className="h-3 mb-6" />
          <div className="grid md:grid-cols-2 gap-3">
            {readinessSteps.map((step) => (
              <div key={step.id} className="flex items-center gap-3 p-3 rounded-lg bg-background/50">
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center ${
                    step.completed ? 'bg-primary' : 'bg-muted'
                  }`}
                >
                  {step.completed && <CheckCircle className="w-3 h-3 text-background" />}
                </div>
                <span className={`text-sm ${step.completed ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {step.text}
                </span>
              </div>
            ))}
          </div>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="matches" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="matches">Funding Matches</TabsTrigger>
            <TabsTrigger value="naics">NAICS Optimizer</TabsTrigger>
            <TabsTrigger value="tradelines">Tier-1 Trade Lines</TabsTrigger>
            <TabsTrigger value="zero">0% Strategy</TabsTrigger>
          </TabsList>

          {/* Funding Matches Tab */}
          <TabsContent value="matches" className="space-y-4">
            <div className="space-y-4">
              {fundingMatches.map((match, index) => (
                <motion.div
                  key={match.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                >
                  <Card className="p-6 hover:border-primary/50 transition-all">
                    <div className="flex items-start justify-between gap-4 mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h4 className="text-lg font-semibold">{match.name}</h4>
                          <Badge className={getMatchColor(match.matchScore)}>
                            {match.matchScore}% Match
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
                          <span className="flex items-center gap-1">
                            <CreditCard className="w-4 h-4" />
                            {match.type}
                          </span>
                          <span className="font-mono font-semibold text-accent">{match.amount}</span>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setSelectedMatch(match.id)}>
                          View Details
                        </Button>
                        <Button size="sm" disabled={match.matchScore < 60}>
                          Apply Now <ExternalLink className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-2">Requirements:</p>
                      <div className="flex flex-wrap gap-2">
                        {match.requirements.map((req, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {req}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          </TabsContent>

          {/* NAICS Optimizer Tab */}
          <TabsContent value="naics" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-2">NAICS Code Optimization</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Your NAICS code affects funding approval rates. Lower-risk codes get better terms.
              </p>
              <div className="space-y-3">
                {naicsCodes.map((code, index) => (
                  <motion.div
                    key={code.code}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                    className={`p-4 rounded-lg border ${
                      code.recommended ? 'border-primary bg-primary/5' : 'border-border'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="font-mono font-semibold">{code.code}</span>
                          <Badge
                            className={
                              code.risk === 'Low'
                                ? 'bg-primary/10 text-primary border-primary/20'
                                : code.risk === 'Medium'
                                ? 'bg-accent/10 text-accent border-accent/20'
                                : 'bg-destructive/10 text-destructive border-destructive/20'
                            }
                          >
                            {code.risk} Risk
                          </Badge>
                          {code.recommended && (
                            <Badge variant="outline" className="border-primary text-primary">
                              Recommended
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">{code.title}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </Card>
          </TabsContent>

          {/* Tier-1 Trade Lines Tab */}
          <TabsContent value="tradelines" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-2">Tier-1 Trade Lines</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Build business credit fast with vendors that report to Dun & Bradstreet, Experian Business, and Equifax Business.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-4 font-semibold">Vendor</th>
                      <th className="text-left py-3 px-4 font-semibold">Credit Limit</th>
                      <th className="text-left py-3 px-4 font-semibold">Payment Terms</th>
                      <th className="text-left py-3 px-4 font-semibold">Reports to Bureaus</th>
                      <th className="text-left py-3 px-4 font-semibold">Difficulty</th>
                      <th className="text-right py-3 px-4 font-semibold">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tradeLines.map((line, index) => (
                      <motion.tr
                        key={line.name}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                        className="border-b border-border hover:bg-muted/30 transition-colors"
                      >
                        <td className="py-4 px-4 font-semibold">{line.name}</td>
                        <td className="py-4 px-4 font-mono">{line.limit}</td>
                        <td className="py-4 px-4">{line.terms}</td>
                        <td className="py-4 px-4">
                          <Badge variant="outline" className="border-primary text-primary">
                            {line.reports}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <Badge
                            className={
                              line.difficulty === 'Easy'
                                ? 'bg-primary/10 text-primary border-primary/20'
                                : 'bg-accent/10 text-accent border-accent/20'
                            }
                          >
                            {line.difficulty}
                          </Badge>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <Button size="sm" variant="outline">
                            Apply <ExternalLink className="w-3 h-3 ml-1" />
                          </Button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          {/* 0% Strategy Tab */}
          <TabsContent value="zero" className="space-y-4">
            <Card className="p-6">
              <h3 className="text-xl font-semibold mb-2">0% Interest Funding Strategy</h3>
              <p className="text-sm text-muted-foreground mb-6">
                Access $50k-$150k in interest-free capital by stacking 0% APR business credit cards and lines of credit.
              </p>
              <div className="space-y-4">
                {zeroPercentSteps.map((step, index) => (
                  <motion.div
                    key={step.phase}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ ...springPresets.gentle, delay: index * 0.1 }}
                    className="p-4 rounded-lg border border-border hover:border-primary/50 transition-all"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <span className="font-bold text-primary">{step.phase}</span>
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold mb-1">{step.title}</h4>
                        <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
                        <Badge variant="outline" className="border-accent text-accent font-mono">
                          {step.amount}
                        </Badge>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
              <div className="mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg">
                <div className="flex items-start gap-3">
                  <TrendingUp className="w-5 h-5 text-accent mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-accent mb-1">Pro Tip</h4>
                    <p className="text-sm text-muted-foreground">
                      Time your applications strategically. Apply for multiple cards within a 14-day window to minimize credit inquiries. Always pay before the 0% period ends to avoid interest charges.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
